const normalizeUri = (value) => (value || '').trim().replace(/\/+$/, '').toLowerCase();

/**
 * OAuth redirect URIs registered for Google Home / Assistant linking (optional).
 * Exact matches win over heuristics.
 */
const GOOGLE_REDIRECT_URIS = [
  ...(process.env.REACT_APP_GOOGLE_REDIRECT_URIS || '').split(','),
  process.env.REACT_APP_GOOGLE_REDIRECT_URI || '',
]
  .map((value) => normalizeUri(value))
  .filter(Boolean);

const ALEXA_REDIRECT_URIS = [
  process.env.REACT_APP_ALEXA_REDIRECT_URI_NA,
  process.env.REACT_APP_ALEXA_REDIRECT_URI_EU,
  process.env.REACT_APP_ALEXA_REDIRECT_URI_JP,
].filter(Boolean);

const LINKING_PROVIDER_STORAGE_KEY = 'linkingProvider';

/**
 * @param {string} redirectUri
 * @returns {'alexa' | 'google'}
 */
export const getLinkingProviderFromRedirectUri = (redirectUri) => {
  const normalized = normalizeUri(redirectUri);
  if (!normalized) {
    return 'alexa';
  }

  if (GOOGLE_REDIRECT_URIS.includes(normalized)) {
    return 'google';
  }

  for (const alexaUri of ALEXA_REDIRECT_URIS) {
    if (normalized === normalizeUri(alexaUri)) {
      return 'alexa';
    }
  }

  if (normalized.includes('googleusercontent.com') || normalized.includes('oauth-redirect.google')) {
    return 'google';
  }

  if (normalized.includes('amazon.com') || normalized.includes('amazon.co.jp')) {
    return 'alexa';
  }

  return 'alexa';
};

export const persistLinkingProviderFromRedirectUri = (redirectUri) => {
  const provider = getLinkingProviderFromRedirectUri(redirectUri);
  sessionStorage.setItem(LINKING_PROVIDER_STORAGE_KEY, provider);
  return provider;
};

/**
 * @param {string} [searchString]
 * @returns {'alexa' | 'google'}
 */
export const readStoredLinkingProvider = (searchString = window.location.search) => {
  const params = new URLSearchParams(searchString);
  const preview = params.get('preview');
  if (preview === 'google' || preview === 'alexa') {
    return preview;
  }

  const stored = sessionStorage.getItem(LINKING_PROVIDER_STORAGE_KEY);
  if (stored === 'google' || stored === 'alexa') {
    return stored;
  }

  const redirectUri = params.get('redirect_uri') || '';
  return getLinkingProviderFromRedirectUri(redirectUri);
};

/**
 * Provider for the login page only: URL query (preview, redirect_uri). Does not read sessionStorage.
 * @param {string} [searchString]
 * @returns {'alexa' | 'google'}
 */
export const getLinkingProviderFromPageContext = (searchString = window.location.search) => {
  const params = new URLSearchParams(searchString);
  const preview = params.get('preview');
  if (preview === 'google' || preview === 'alexa') {
    return preview;
  }
  const redirectUri = params.get('redirect_uri') || '';
  return getLinkingProviderFromRedirectUri(redirectUri);
};
